import { mapState } from 'vuex';
import ApiService from '@/services/apiService';
import MapViewMixin from '@/components/core/dtwin/map/viewMixin';
import listOptionsPersistenceMixin from '@/components/custom/listOptionsPersistenceMixin';
import healthStatusMixin from '@/components/core/virtualDevice/healthStatusMixin';
import multiSearchMixin from '@/mixins/multiSearchVirtualDevices';
import get from 'lodash/get';
import moment from 'moment';
export default {
  name: 'ViewVirtualDevicesMap',
  mixins: [MapViewMixin, listOptionsPersistenceMixin, multiSearchMixin, healthStatusMixin],
  data: function () {
    return {
      disableFetchOnCreatedMixin: true,
      lastSearch: null,
      // share the list-persistence with the virtual-device-list
      listPersistencePath: 'VirtualDevice.list',
      searchMode: 'multi',
      searchOperator: 'AND',
      historyFrom: null,
      initialAccounts: [],
      listIsReadyToFetch: false
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color']),
    requestFilter() {
      if (this.selectedAccounts.length === 0) {
        return [];
      }
      return [{
        type: 'tenantIds',
        ids: this.selectedAccounts
      }];
    }
  },
  watch: {
    selectedAccounts() {
      this.$refs.mapInstance.debouncedRequestClusterRequest();
      this.fetchGeoMetaInfos();
    },
    // language-check to update search-types in searchTypesMixin
    listIsReadyToFetch(newV, oldV) {
      if (newV && !oldV) {
        this.loadInitialData();
      }
    }
  },
  mounted() {
    this.historyFrom = moment(new Date()).subtract(2, 'months').unix();
    const {
      view,
      mode
    } = this.$route.query;
    if (!view) {
      this.listIsReadyToFetch = true;
    } else {
      this.listIsReadyToFetch = false;
      this.setUpMonitoringViewList(view, mode);
    }
  },
  methods: {
    setUpMonitoringViewList(viewId, mode) {
      this.$router.replace({
        query: null
      });
      ApiService.virtualDeviceViewGet(this, viewId).then(data => {
        const search = data.viewConfig.searchItems;
        this.searchOperator = data.viewConfig.searchOperator;
        this.searchItems = search.map(item => ({
          param: item.variable,
          search: item.search
        }));

        // persist search items and operator in store
        this.$store.dispatch('app/updateVirtualDeviceListSearch', {
          items: this.searchItems,
          operator: this.searchOperator
        });
        if (mode === 'warnings') {
          this.searchItems.push({
            param: 'health',
            search: {
              value: '1',
              text: 'Warning'
            }
          });
        }
        if (mode === 'errors') {
          this.searchItems.push({
            param: 'health',
            search: {
              value: '2',
              text: 'Alert'
            }
          });
        }
        const accounts = [data.tenantId];
        this.initialAccounts = accounts;
        this.$nextTick(() => {
          this.listIsReadyToFetch = true;
        });
      }).catch(() => {
        this.listIsReadyToFetch = true;
        this.$snack(this.$i18n_t('DigitalTwin.VirtualDevice.snacks.loadMonitoringViewFailed'), 'error');
      });
    },
    handleHistoryFromPicked(dateObject) {
      this.historyFrom = dateObject.from;
    },
    breadCrumbsPath() {
      return [{
        to: '/virtual-devices',
        text: this.$i18n_t('Common.drawer.virtualDevices')
      }, this.$i18n_t('DigitalTwin.VirtualDevice.map.title')];
    },
    searchUpdated(search, searchOperator) {
      this.searchItems = search;
      this.searchOperator = searchOperator;
      this.fetchList();
    },
    // from listPersistenceMixin, forward to cluster-request
    fetchList() {
      if (this.$refs && this.$refs.mapInstance) {
        this.$refs.mapInstance.debouncedRequestClusterRequest();
      }
      return this.fetchGeoMetaInfos();
    },
    fetchGeoMetaInfos() {
      const searchItems = this.searchItems.map(item => ({
        variable: item.param,
        // support item.search.value === null, so do not simply have a >search.value || search< expression
        search: (item.search || {}).value === undefined ? item.search : item.search.value,
        key: item.key
      }));
      const filters = [{
        type: 'tenantIds',
        ids: this.selectedAccounts
      }];
      return ApiService.virtualDeviceFetchGeoMetaInfos(this, filters, searchItems, this.searchOperator).then(data => {
        this.stateKeys = data.keyList;
        this.floorList = data.floorList;
      }).catch(error => {
        console.error('Error loading map-twin-state-keys', error);
      });
    },
    fetchTwinData() {
      this.loadTree();
      return this.fetchGeoMetaInfos();
    },
    resetDefaults() {
      this.selectedStateKey = null;
      this.gradientPolygonColours = false;
    },
    valuesToPersist() {
      return {
        gradientPolygonColours: this.gradientPolygonColours,
        excludeZones: this.excludeZones,
        selectedStateKey: this.selectedStateKey
      };
    },
    getClusterContent(cluster, bounds) {
      if (!cluster) {
        this.selectedCluster = {};
        return;
      }
      const searchItems = this.searchItems.map(item => ({
        variable: item.param,
        // support item.search.value === null, so do not simply have a >search.value || search< expression
        search: (item.search || {}).value === undefined ? item.search : item.search.value,
        key: item.key
      }));
      const filters = [{
        type: 'tenantIds',
        ids: this.selectedAccounts
      }];
      ApiService.virtualDeviceGetGeoClusterContent(this, cluster.id, this.indoorFloorDtwinId, [[bounds['_ne'].lng, bounds['_ne'].lat], [bounds['_sw'].lng, bounds['_sw'].lat]], filters, searchItems).then(data => {
        this.selectedCluster = {
          ...cluster,
          items: data.vdevices
        };
      }).catch(error => {
        console.error('Error loading map-twin-clusters', error);
      });
    },
    loadTree(bounds = undefined) {
      this.$store.state.app.load = 10;
      this.$store.state.app.loading = true;
      const searchItems = this.searchItems.map(item => ({
        variable: item.param,
        // support item.search.value === null, so do not simply have a >search.value || search< expression
        search: (item.search || {}).value === undefined ? item.search : item.search.value,
        key: item.key
      }));
      const filters = [{
        type: 'tenantIds',
        ids: this.selectedAccounts
      }];

      // persist options for next call of this page (during this browser-tab-session)
      this.$store.state.app.pageFilters = {
        ...this.$store.state.app.pageFilters,
        VirtualDevice: {
          ...(get(this.$store.state.app, 'pageFilters.VirtualDevice') || {}),
          list: {
            // do not overwrite the table-options here
            ...(get(this.$store.state.app, 'pageFilters.VirtualDevice.list') || {}),
            searchDetails: this.searchItems,
            searchOperator: this.searchOperator
          }
        }
      };
      return ApiService.virtualDeviceFetchGeoClusters(this, this.indoorFloorDtwinId, bounds, this.selectedStateKey, filters, searchItems, {
        hideStatelessPolygons: this.hideStatelessPolygons
      }, this.searchOperator).then(data => {
        this.calculateBoundingBox(data);
        this.markerItems = data.vdevices;
      }).catch(error => {
        console.error('Error loading map-twin-clusters', error);
      }).finally(() => {
        this.$store.state.app.loading = false;
      });
    }
  }
};